import React, { useState, useEffect } from 'react';
import { useSelector } from 'react-redux';
import { 
    Dialog, 
    DialogTitle, 
    DialogContent, 
    DialogActions, 
    Button, 
    TextField, 
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    CircularProgress,
    Box,
    Typography,
    InputAdornment
} from '@mui/material';
import EditNoteIcon from '@mui/icons-material/EditNote';
import { FinanceService } from '../../../api/services/financeService';
import { SettingsService } from '../../../api/services/settingsService';
import { toast } from '../../../utils/toast';
import { useCurrency, formatCurrency } from '../../../utils/currency';
import { isUaeProperty, DUBAI_DEFAULT_RATES, calculateDubaiItemizedChargeTax, reverseDubaiItemizedChargeTax } from '../../../utils/dubaiHospitalityTax';

const CATEGORY_MAPPING = {
    'Room Charge': 'room',
    'Food & Beverage (F&B)': 'f_and_b',
    'F&B': 'f_and_b',
    'Spa & Wellness': 'spa_service',
    'Extra Bed': 'extra_bed',
    'Laundry Services': 'miscellaneous',
    'Parking Fee': 'miscellaneous',
    'Room Damage Fee': 'miscellaneous',
    'Cancellation Fee': 'cancellation',
    'Other / Miscellaneous': 'miscellaneous'
};

export default function EditFolioChargeModal({ open, onClose, folioId: drawerFolioId, charge, onSuccess, isUae: isUaeProp, tenantId: propTenantId }) {
    const { user, tenants } = useSelector(state => state.auth || { tenants: [] });
    const authData = (() => {
        try {
            return JSON.parse(localStorage.getItem('adminAuth') || '{}');
        } catch (_) {
            return {};
        }
    })();
    const tenantId = propTenantId || user?.tenantId || authData?.tenantId || '1';
    const activeTenant = (tenants || []).find(t => 
        String(t.tenantId || t.id || t._id) === String(tenantId) ||
        String(t.id || t._id) === String(authData?.hotelId || user?.hotelId)
    );
    const hotel = activeTenant || authData?.hotel || user?.hotel || authData?.tenant || authData?.property || {};
    const { currency, currencySymbol } = useCurrency();
    const isUae = isUaeProp !== undefined ? isUaeProp : (
                  currency === 'AED' || 
                  hotel.currency === 'AED' || 
                  authData?.currency === 'AED' ||
                  hotel.country === 'UAE' || 
                  hotel.city === 'Dubai' || 
                  String(hotel.country || '').trim().toUpperCase() === 'UAE' || 
                  String(hotel.city || '').trim().toLowerCase() === 'dubai' || 
                  isUaeProperty(hotel) ||
                  isUaeProperty(authData));

    const [isUpdating, setIsUpdating] = useState(false);
    const [form, setForm] = useState({
        category: '',
        description: '',
        amount: ''
    });
    const [activeTaxes, setActiveTaxes] = useState([]);
    const [taxAmount, setTaxAmount] = useState(0);
    const [taxDetails, setTaxDetails] = useState(null);
    const [originalBaseAmount, setOriginalBaseAmount] = useState(null);
    const hasUserEditedRef = React.useRef(false);

    const resolveChargeBaseAmount = (chargeItem, isUaeTenant, taxes = []) => {
        if (!chargeItem) return '';
        const rawTotal = Math.abs(Number(
            chargeItem.amount !== undefined && chargeItem.amount !== null
                ? chargeItem.amount
                : (chargeItem.value !== undefined ? chargeItem.value : (chargeItem.baseAmount || 0))
        ));

        if (!rawTotal || isNaN(rawTotal)) return '';

        // If charge carries an explicit baseAmount less than the gross total amount, use it directly
        const candidates = [
            chargeItem.baseAmount,
            chargeItem.base,
            chargeItem.netAmount,
            chargeItem.subtotal,
            chargeItem.preTaxAmount
        ];
        for (const cand of candidates) {
            const num = Number(cand);
            if (!isNaN(num) && num > 0 && num < rawTotal) {
                return String(num % 1 === 0 ? num : Number(num.toFixed(2)));
            }
        }

        if (isUaeTenant) {
            const rev = reverseDubaiItemizedChargeTax({
                totalAmount: rawTotal,
                category: chargeItem.category || chargeItem.description,
                taxes
            });
            if (rev && rev.base > 0) {
                return String(rev.base % 1 === 0 ? rev.base : Number(rev.base.toFixed(2)));
            }
        }

        return String(rawTotal % 1 === 0 ? rawTotal : Number(rawTotal.toFixed(2)));
    };

    useEffect(() => {
        if (open && charge) {
            hasUserEditedRef.current = false;
            const resolvedBase = resolveChargeBaseAmount(charge, isUae, activeTaxes);

            setForm({
                category: charge.category || 'Food & Beverage (F&B)',
                description: charge.description || charge.item || '',
                amount: resolvedBase
            });
            setActiveTaxes([]);
            setTaxAmount(0);
            setTaxDetails(null);
            setOriginalBaseAmount(resolvedBase ? Number(resolvedBase) : null);
        } else if (!open) {
            hasUserEditedRef.current = false;
        }
    }, [open, charge, isUae]);

    useEffect(() => {
        const fetchTaxes = async () => {
            const currentCat = form.category || charge?.category;
            if (!open || !currentCat) {
                setActiveTaxes([]);
                setTaxAmount(0);
                return;
            }
            try {
                if (isUae) {
                    const result = await SettingsService.getTaxes(tenantId, { noPagination: true });
                    const slabs = Array.isArray(result) ? result : (result.data || result.taxes || []);
                    let activeSlabs = slabs.filter(t => t.status === 'Active');
                    setActiveTaxes(activeSlabs);

                    // Refine base calculation with active tax slabs if user has not manually modified the amount
                    if (!hasUserEditedRef.current && charge) {
                        const refinedBase = resolveChargeBaseAmount(charge, true, activeSlabs);
                        if (refinedBase) {
                            setForm(prev => ({ ...prev, amount: refinedBase }));
                        }
                    }
                    return;
                }

                const backendCategory = CATEGORY_MAPPING[currentCat] || currentCat || 'miscellaneous'; 
                const result = await SettingsService.getTaxes(tenantId, { serviceType: backendCategory });
                const slabs = Array.isArray(result) ? result : (result.data || []);
                let active = slabs.filter(t => t.status === 'Active');
                
                // Statutory fallback if DB slab setting is not found
                if (active.length === 0) {
                    if (backendCategory === 'f_and_b_specified') {
                        active.push({
                            serviceType: 'f_and_b_specified',
                            gstPct: 18,
                            cgstPct: 9,
                            sgstPct: 9,
                            minThreshold: 0,
                            maxThreshold: 999999999,
                            status: 'Active'
                        });
                    } else if (backendCategory === 'f_and_b') {
                        active.push({
                            serviceType: 'f_and_b',
                            gstPct: 5,
                            cgstPct: 2.5,
                            sgstPct: 2.5,
                            minThreshold: 0,
                            maxThreshold: 999999999,
                            status: 'Active'
                        });
                    }
                }
                
                setActiveTaxes(active);

                // If non-UAE, check if raw amount included GST
                if (!hasUserEditedRef.current && charge) {
                    const rawTotal = Math.abs(Number(charge.amount || charge.value || 0));
                    const explicitBase = [charge.baseAmount, charge.base, charge.netAmount, charge.subtotal].map(v => Number(v)).find(v => !isNaN(v) && v > 0 && v < rawTotal);
                    if (explicitBase !== undefined) {
                        setForm(prev => ({ ...prev, amount: String(explicitBase) }));
                    } else if (active.length > 0 && rawTotal > 0) {
                        const tax = active[0];
                        const pct = parseFloat(tax.rate !== undefined && tax.rate !== null && tax.rate !== '' ? tax.rate : (tax.gstPct || 0));
                        if (pct > 0) {
                            const calculatedBase = Number((rawTotal / (1 + pct / 100)).toFixed(2));
                            const checkTotal = Number((calculatedBase + (calculatedBase * pct) / 100).toFixed(2));
                            if (Math.abs(checkTotal - rawTotal) <= 0.05) {
                                setForm(prev => ({ ...prev, amount: String(calculatedBase % 1 === 0 ? calculatedBase : calculatedBase) }));
                            }
                        }
                    }
                }
            } catch (err) {
                console.error("Failed to fetch taxes", err);
                setActiveTaxes([]);
            }
        };
        fetchTaxes();
    }, [open, form.category, charge, tenantId, isUae]);

    useEffect(() => {
        const amount = parseFloat(form.amount) || 0;
        if (amount <= 0 || activeTaxes.length === 0) {
            setTaxAmount(0);
            return;
        }

        let applicableTax = null;
        for (const tax of activeTaxes) {
            const min = parseFloat(tax.minThreshold || 0);
            const max = parseFloat(tax.maxThreshold || 999999999);
            if (amount >= min && amount <= max) {
                applicableTax = tax;
                break;
            }
        }

        if (applicableTax) {
            const pct = parseFloat(applicableTax.rate !== undefined && applicableTax.rate !== null && applicableTax.rate !== '' ? applicableTax.rate : (applicableTax.gstPct || 0));
            setTaxAmount((amount * pct) / 100);
            setTaxDetails(applicableTax);
        } else {
            setTaxAmount(0);
            setTaxDetails(null);
        }
    }, [form.amount, activeTaxes]);

    const uaeBreakdown = (isUae && parseFloat(form.amount || 0) > 0)
        ? calculateDubaiItemizedChargeTax({ baseAmount: form.amount, category: form.category || charge?.category || charge?.description, taxes: activeTaxes })
        : null;

    const handleTextChange = (field) => (event) => {
        setForm(prev => ({ ...prev, [field]: event.target.value }));
    };

    const handleAmountChange = (e) => {
        const val = e.target.value;
        if (!val || val.match(/^\d*\.?\d*$/)) {
            hasUserEditedRef.current = true;
            setForm(prev => ({ ...prev, amount: val }));
        }
    };

    const handleSubmit = async () => {
        if (!form.category || !form.description || !form.amount) {
            toast.error('Please fill out all required fields.');
            return;
        }

        const amountVal = parseFloat(form.amount);
        if (isNaN(amountVal) || amountVal <= 0) {
            toast.error('Charge amount must be a positive number greater than zero.');
            return;
        }

        setIsUpdating(true);
        try {
            const payload = {
                amount: amountVal,
                category: form.category,
                description: form.description
            };

            const chargeId = charge.id || charge.chargeId || charge._id;
            const folioId = drawerFolioId || charge.folioId;
            await FinanceService.editFolioCharge(folioId, chargeId, payload);
            toast.success('Folio charge details modified successfully');
            onSuccess();
            onClose();
        } catch (error) {
            console.error('Failed to edit manual charge:', error);
            // Handled via global interceptor
        } finally {
            setIsUpdating(false);
        }
    };

    return (
        <Dialog open={open} onClose={!isUpdating ? onClose : undefined} maxWidth="sm" fullWidth PaperProps={{ sx: { borderRadius: 3 } }}>
            <DialogTitle sx={{ borderBottom: '1px solid #eee', bgcolor: '#fbfbfb', display: 'flex', alignItems: 'center' }}>
                <EditNoteIcon sx={{ mr: 1, color: 'primary.main' }} /> Edit Folio Charge
            </DialogTitle>
            <DialogContent sx={{ p: 4 }}>
                <Box sx={{ mt: 2 }}>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                        Modify the description, category, and cost valuation of this specific ledger entry.
                    </Typography>

                    <FormControl fullWidth sx={{ mb: 3 }} required>
                        <InputLabel>Charge Category</InputLabel>
                        <Select
                            value={form.category}
                            label="Charge Category"
                            onChange={handleTextChange('category')}
                            disabled={isUpdating}
                        >
                            {Array.from(new Set([
                                'Food & Beverage (F&B)',
                                'Spa & Wellness',
                                'Extra Bed',
                                'Laundry Services',
                                'Parking Fee',
                                'Room Damage Fee',
                                'Cancellation Fee',
                                'Other / Miscellaneous',
                                form.category
                            ])).filter(Boolean).map(cat => (
                                <MenuItem key={cat} value={cat}>{cat}</MenuItem>
                            ))}
                        </Select>
                    </FormControl>

                    <TextField
                        fullWidth
                        required
                        label="Line-Item Description"
                        placeholder="e.g., Updated dinner price"
                        value={form.description}
                        onChange={handleTextChange('description')}
                        sx={{ mb: 3 }}
                        disabled={isUpdating}
                    />

                    <TextField
                        fullWidth
                        required
                        label="Charge Amount (Base)"
                        placeholder="0.00"
                        value={form.amount}
                        onChange={handleAmountChange}
                        disabled={isUpdating}
                        InputProps={{
                            startAdornment: <InputAdornment position="start">{currencySymbol}</InputAdornment>,
                        }}
                    />
                    
                    {parseFloat(form.amount || 0) > 0 && (
                        <Box sx={{ mt: 2, p: 2, bgcolor: '#f8fafc', borderRadius: 2, border: '1px solid #e2e8f0' }}>
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                                <Typography variant="body2" color="text.secondary">Charge Amount (Base)</Typography>
                                <Typography variant="body2" color="text.primary">{formatCurrency(parseFloat(form.amount || 0), currency)}</Typography>
                            </Box>

                            {isUae && uaeBreakdown ? (
                                <>
                                    <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.75 }}>
                                        <Typography variant="body2" color="text.secondary">
                                            Service Charge ({uaeBreakdown.serviceChargePct}%)
                                        </Typography>
                                        <Typography variant="body2" color="text.primary">
                                            {uaeBreakdown.isCancellation ? 'Exempt (0.00)' : `+ ${formatCurrency(uaeBreakdown.serviceChargeAmount, currency)}`}
                                        </Typography>
                                    </Box>
                                    <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.75 }}>
                                        <Typography variant="body2" color="text.secondary">
                                            Dubai Municipality Fee ({uaeBreakdown.municipalityFeePct}%)
                                        </Typography>
                                        <Typography variant="body2" color="text.primary">
                                            {uaeBreakdown.isCancellation ? 'Exempt (0.00)' : `+ ${formatCurrency(uaeBreakdown.municipalityFeeAmount, currency)}`}
                                        </Typography>
                                    </Box>
                                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 0.75 }}>
                                        <Box>
                                            <Typography variant="body2" color="text.secondary">
                                                UAE VAT ({uaeBreakdown.vatPct}% on {uaeBreakdown.isCancellation ? 'Base' : 'Subtotal'})
                                            </Typography>
                                            {!uaeBreakdown.isCancellation && (
                                                <Typography variant="caption" sx={{ color: 'text.disabled', display: 'block', fontSize: '0.68rem' }}>
                                                    Subtotal before VAT: {formatCurrency(uaeBreakdown.subtotalBeforeVat, currency)}
                                                </Typography>
                                            )}
                                        </Box>
                                        <Typography variant="body2" color="text.primary">+ {formatCurrency(uaeBreakdown.vatAmount, currency)}</Typography>
                                    </Box>
                                    <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                                        <Typography variant="body2" color="text.secondary">Tourism Dirham</Typography>
                                        <Typography variant="caption" sx={{ color: '#059669', fontWeight: 600, mt: 0.25 }}>Exempt (0.00 AED)</Typography>
                                    </Box>
                                </>
                            ) : (
                                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1 }}>
                                    <Box>
                                        <Typography variant="body2" color="text.secondary">Estimated Tax</Typography>
                                        {taxDetails && ((taxDetails.rate !== undefined && taxDetails.rate > 0) || (taxDetails.gstPct > 0)) && (
                                            <Typography variant="caption" sx={{ color: 'text.disabled', display: 'block', mt: 0.25, fontSize: '0.7rem' }}>
                                                {`Includes ${parseFloat(taxDetails.cgstPct || 0)}% CGST & ${parseFloat(taxDetails.sgstPct || 0)}% SGST`}
                                            </Typography>
                                        )}
                                    </Box>
                                    <Typography variant="body2" color="text.primary" sx={{ mt: 0.25 }}>+ {formatCurrency(taxAmount, currency)}</Typography>
                                </Box>
                            )}

                            <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 1.5, pt: 1.5, borderTop: '1px dashed #cbd5e1' }}>
                                <Typography variant="subtitle2" fontWeight="bold">Total Post Amount</Typography>
                                <Typography variant="subtitle2" color="primary.main" fontWeight="bold">
                                    {formatCurrency((isUae && uaeBreakdown) ? uaeBreakdown.totalPostAmount : (parseFloat(form.amount || 0) + taxAmount), currency)}
                                </Typography>
                            </Box>
                        </Box>
                    )}
                </Box>
            </DialogContent>
            <DialogActions sx={{ p: 3, borderTop: '1px solid #eee', bgcolor: '#fbfbfb' }}>
                <Button onClick={onClose} color="inherit" disabled={isUpdating}>
                    Cancel
                </Button>
                <Button 
                    onClick={handleSubmit} 
                    variant="contained" 
                    color="primary"
                    disabled={isUpdating}
                    startIcon={isUpdating ? <CircularProgress size={20} color="inherit" /> : null}
                    sx={{ px: 3 }}
                >
                    {isUpdating ? 'Updating...' : 'Save Changes'}
                </Button>
            </DialogActions>
        </Dialog>
    );
}
